import React, { useEffect, useState } from "react";
import { useClickTracker } from "web/react/hooks/use-click-tracker/use-click-tracker";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import userProfiler from "web/script/modules/userprofiler";
import globals from "web/script/modules/globals";
import browser from "web/script/utils/browser";
import store from "web/redux/store";
import { useGetSaveForLaterStatus } from "web/react/hooks/use-save-for-later/use-save-for-later";
import { openOOSModal } from "web/redux/ducks/send-to-app-modal";

function constructHref(
    href: string,
    pageViewID?: string,
    reason?: string,
    type?: string,
    fromUrl?: string
): string {
    let parsed = new URL(href, globals.window.location.href);

    if (pageViewID) {
        parsed.searchParams.set("pageViewId", pageViewID);
    }
    if (reason) {
        parsed.searchParams.set("reason", reason);
    }
    if (type) {
        parsed.searchParams.set("type", type);
    }
    if (fromUrl) {
        parsed.searchParams.set("fromUrl", fromUrl);
    }

    return parsed.toString();
}

/**
 * A custom hook for keeping a lead URL up to date with analytics data
 * @param {string} href The lead URL to track
 * @param {string} [reason] Optional lead reason
 * @param {string} [type] Optional lead type
 * @param {string} [fromUrl] Optional origin URL
 * @returns {string} The augmented lead URL
 */
export function useLeadHref(
    href: string,
    reason?: string,
    type?: string,
    fromUrl?: string
): string {
    const [leadHref, setLeadHref] = useState<string>(href);

    useEffect(() => {
        function updateHref(): void {
            let pageViewID = analytics.getPageViewId();
            setLeadHref(constructHref(href, pageViewID, reason, type, fromUrl));
        }
        updateHref();
        analytics.on("pageViewIdChanged", updateHref);

        return function cleanup(): void {
            analytics.off("pageViewIdChanged", updateHref);
        };
    }, [fromUrl, href, reason, type]);

    return leadHref;
}

interface LeadLinkProps {
    href: string;
    children?: React.ReactElement | string;
    eventCategory?: string;
    eventAction?: string;
    eventLabel?: string;
    productId?: string;
    reason?: string;
    beforeOnClick?: (
        processClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void,
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => void;
    afterOnClick?: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    className?: string;
    fromUrl?: string;
    openInNewTab?: boolean;
    type?: string;
    affiliatePromoUrl?: string;
    rel?: string;
}

function LeadLink({
    href,
    children,
    eventCategory,
    eventAction,
    eventLabel,
    productId,
    reason,
    beforeOnClick,
    afterOnClick,
    className,
    fromUrl,
    openInNewTab,
    type,
    affiliatePromoUrl,
    rel,
}: LeadLinkProps): React.ReactElement {
    const isMobile = browser.getViewport() === browser.VIEWPORTS.MOBILE;
    const pendingPromises = React.useRef<Promise<void>[] | any[]>([]);
    let [isSavedForLater] = useGetSaveForLaterStatus(productId);
    let leadHref = useLeadHref(href, reason, type, fromUrl);
    let savedForLaterClickTracker = useClickTracker(
        "affiliate",
        "lead",
        "save_for_later_buy_on_store",
        !openInNewTab
    );
    let trackOnClick = useClickTracker(eventCategory, eventAction, eventLabel, !openInNewTab);
    let showPromoCode = affiliatePromoUrl && environment.get("showPromoCode") ? true : false;
    let shouldOpenOOSModal =
        reason === "affiliate-buy-on-store-button" &&
        isMobile &&
        browser.ios &&
        !userProfiler.isLoggedIn() &&
        environment.getFeature("mg_oos_app_or_retailer") &&
        environment.getFeature("mg_oos_app_or_retailer") !== "no_block";

    // Generate href for tracked button from the props
    function processClick(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void {
        event.preventDefault();

        if (productId && reason) {
            userProfiler.saveClickedLead(productId, reason);
        }

        if (isSavedForLater) {
            pendingPromises.current.push(savedForLaterClickTracker(event));
        }

        if (trackOnClick) {
            pendingPromises.current.push(trackOnClick(event));
        }

        if (showPromoCode) {
            globals.window.open(affiliatePromoUrl, "_blank");
        }

        if (showPromoCode || !openInNewTab) {
            Promise.all(pendingPromises.current).then(() => {
                globals.window.location.replace(leadHref);
            });
        } else {
            if (shouldOpenOOSModal) {
                store.dispatch(openOOSModal());
                return;
            }
            // redirect-to-retailer looks for globals.window.opener which is set to null in ios
            // if we use the normal href and target so we need to trigger open in new tab
            // using window.open instead to get the correct behavior
            globals.window.open(leadHref, "_blank");
        }

        afterOnClick && afterOnClick(event);
    }

    function onClick(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void {
        if (beforeOnClick) {
            beforeOnClick(processClick, event);
        } else {
            processClick(event);
        }
    }

    return (
        <a
            className={className}
            href={leadHref}
            rel={rel || undefined}
            onClick={onClick}
            target={openInNewTab ? "_blank" : undefined}
            dangerouslySetInnerHTML={
                typeof children === "string" ? { __html: children } : undefined
            }
        >
            {typeof children !== "string" ? children : null}
        </a>
    );
}

export default LeadLink;

LeadLink.defaultProps = {
    openInNewTab: false,
};
